/**
 * Rimettere in piedi una LINEA: una sequenza di semi-mosse conservata in forma
 * compatta (`LineStep`) e rigiocata contro la posizione da cui parte.
 *
 * È il gemello di `applyParsedOpening` ([opening.ts]) e ne segue il patto — *si
 * ferma alla prima semi-mossa che non riconosce* — con due differenze: qui le
 * catture disambiguano, e quello che torna sono le `Move` CANONICHE prodotte da
 * `getAllLegalMoves`, non i passi di partenza. Chi rigioca una linea salvata non
 * si fida di quello che gli arriva: lo risolve contro le mosse legali vere.
 *
 * Serve a tutti e tre i lati — il server lo usa per validare prima di scrivere in
 * libreria, il client per riaprire uno studio sulla damiera d'analisi.
 */

import { GameStatus, type BoardState, type LineStep, type Move } from '@draughts/shared';
import { getAllLegalMoves } from '../moves/capture-moves.js';
import { applyMove } from './move-execution.js';

/** Esito del replay: dove si è arrivati, e fin dove la linea ha retto. */
export interface ReplayedLine {
  /** La posizione dopo l'ultima semi-mossa riconosciuta. */
  board: BoardState;
  /** Le mosse vere, nell'ordine in cui sono state giocate. */
  moves: Move[];
  /** Le stesse mosse in forma compatta e CANONICA: è questa che si conserva. */
  steps: LineStep[];
}

/** Le caselle catturate, ordinate: un insieme confrontabile senza allocare un Set. */
function captureKey(squares: readonly number[]): string {
  return [...squares].sort((a, b) => a - b).join(',');
}

/**
 * La mossa VERA che corrisponde a una semi-mossa conservata, cercata fra le mosse
 * legali della posizione. `undefined` quando quella semi-mossa non è giocabile qui.
 *
 * È il punto in cui `(from, to)` smette di bastare. Due linee possono condividere
 * partenza e arrivo e mangiare pezzi DIVERSI — succede a ogni dama volante, e nella
 * frisone anche alle pedine — e allora `captures` disambigua. L'insieme, non
 * l'ordine: se restano più candidati anche dopo, si prende il primo, e non è una
 * resa, perché a parità di partenza, arrivo e insieme catturato la posizione
 * risultante è la stessa — cambia solo il `path`, cioè l'animazione. (Dopo il
 * collasso delle prese equivalenti in `getAllLegalMoves` quel caso non arriva
 * nemmeno più qui, ma il ripiego resta: è ciò che rende `captures` OPZIONALE, e
 * quindi compatibile con chi non lo spedisce.)
 *
 * Un insieme di catture che non esiste fra le mosse legali NON ripiega sul primo
 * candidato: ritorna `undefined`. Indovinare sarebbe peggio che fermarsi.
 *
 * Lo usano i tre lati che devono ritrovare una mossa da una forma compatta: il
 * replay di una linea qui sotto, il validatore dei problemi e il servizio dei
 * problemi — che prima risolvevano su `(from, to)` e potevano applicare una linea
 * diversa da quella giocata.
 */
export function resolveLineStep(
  legalMoves: readonly Move[],
  step: LineStep,
): Move | undefined {
  const candidates = legalMoves.filter((m) => m.from === step.from && m.to === step.to);
  if (step.captures === undefined || candidates.length <= 1) return candidates[0];
  const wanted = captureKey(step.captures);
  return candidates.find((m) => captureKey(m.captures.map((c) => c.square)) === wanted);
}

/**
 * Gioca i passi indicati, risolvendo ciascuno fra le mosse legali della posizione.
 *
 * Si ferma — senza lanciare — quando un passo non trova nessuna mossa legale, e
 * quando la partita è finita: `getAllLegalMoves` restituisce mosse anche su una
 * board già `DRAW` (il contatore frisone la chiude mentre le mosse ci sono),
 * ma `applyMove` le rifiuta, e un troncamento silenzioso è meglio di un'eccezione
 * dentro al caricamento di uno studio.
 */
export function replayLine(board: BoardState, steps: readonly LineStep[]): ReplayedLine {
  let current = board;
  const moves: Move[] = [];

  for (const step of steps) {
    if (current.status !== GameStatus.PLAYING) break;

    const chosen = resolveLineStep(getAllLegalMoves(current), step);

    if (!chosen) break;
    current = applyMove(current, chosen);
    moves.push(chosen);
  }

  return { board: current, moves, steps: toLineSteps(moves) };
}

/** La forma da conservare: `captures` solo dove serve davvero, cioè dove si mangia. */
export function toLineSteps(moves: readonly Move[]): LineStep[] {
  return moves.map((m) => (
    m.captures.length > 0
      ? { from: m.from, to: m.to, captures: m.captures.map((c) => c.square) }
      : { from: m.from, to: m.to }
  ));
}
